import { useDecision } from '@optimizely/react-sdk'
import { useAuthUser } from "react-auth-kit";
import { matchPath, useLocation } from 'react-router-dom'

/**
 * Generates an `onClick` handler for preview buttons that opens the game preview in a new tab.
 * Dynamically constructs the preview URL based on the current route (e.g., challenges, leaderboard)
 * and active feature toggles (Optimizely).
 * 
 * @param {Object} params
 * @param {string} params.code - The game code used for the preview URL.
 * @param {string} [params.path=''] - Optional specific path to append to the preview URL.
 * @returns {{ onClick: Function }} An object containing the onClick handler for the button.
 */
const usePreviewButton = ({ code, path = '' }) => {
    const [decisionPreviewLink] = useDecision('preview-links')
    const location = useLocation()
    const auth = useAuthUser()();
    const { token } = auth || {};
    let suffix = ''
    let prefix = ''

    // Handle side navigation preview links.
    // Wrapped within `preview-links` feature flag.
    if (!path && !suffix && decisionPreviewLink?.enabled) {
        const matchRoute = path => matchPath(location?.pathname, path)
        switch (true) {
            case matchRoute('/games/edit/:game_id')?.isExact:
                suffix = `game/${matchRoute('/games/edit/:game_id')?.params?.game_id}`
                break
            case matchRoute('/tasks/organize/:game_id')?.isExact:
                suffix = `challenges`
                break
            case matchRoute('/teams/leaderboard/:game_id')?.isExact:
                suffix = `leaderboard`
                break
            default:
                suffix = ``
        }
    }

    if (!decisionPreviewLink?.enabled) {
        prefix = `preview/${code}/`
    }

    return {
        onClick: () => {
            window.open(
                `${process.env.REACT_APP_PREVIEW_URL
                }/#/${prefix}${path}${suffix}?code=${code}&token=${token}&preview=yes`,
                '_blank'
            )
        }
    }
}

export default usePreviewButton